import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useBrand } from "../../contexts/BrandContext";
import SEO from "../../components/SEO";

export default function Contact() {
  const { mode, brand } = useBrand();
  const [page, setPage] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const slug = mode === "brand" ? brand.slug : "_all";
    api.get(`/pages/contact/${slug}`).then((r) => setPage(r.data));
  }, [mode, brand]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/submissions`, {
        type: "contact",
        ...form,
        brand_slug: mode === "brand" ? brand.slug : null,
      });
      toast.success("Mesajınız iletildi. Teşekkürler.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const extras = page?.extras || {};

  return (
    <div className="fade-in-up" data-testid="contact-page">
      <SEO title="İletişim" />
      <div className="border-b border-white/10 py-16 bg-gradient-to-b from-[#0A0A0A] to-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">İletişim</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter">Bize Ulaşın</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2 space-y-6">
          {extras.address && (
            <div className="flex items-start gap-3 text-sm">
              <MapPin size={18} className="text-zinc-500 mt-0.5 shrink-0" />
              <div className="text-zinc-300 whitespace-pre-wrap">{extras.address}</div>
            </div>
          )}
          {extras.phone && (
            <div className="flex items-center gap-3 text-sm">
              <Phone size={18} className="text-zinc-500 shrink-0" />
              <div className="text-zinc-300">{extras.phone}</div>
            </div>
          )}
          {extras.email && (
            <div className="flex items-center gap-3 text-sm">
              <Mail size={18} className="text-zinc-500 shrink-0" />
              <div className="text-zinc-300">{extras.email}</div>
            </div>
          )}
          {extras.map_embed && (
            <div
              className="aspect-square border border-white/10 overflow-hidden mt-6"
              dangerouslySetInnerHTML={{ __html: extras.map_embed }}
              data-testid="contact-map"
            />
          )}
          {!extras.address && !extras.phone && !extras.email && (
            <div className="text-zinc-500 text-sm">
              info@noonfoto.com<br />+90 (212) 000 00 00
            </div>
          )}
        </div>

        <form onSubmit={submit} className="lg:col-span-3 border border-white/10 bg-[#0A0A0A] p-6" data-testid="contact-form">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-6">Mesaj Gönder</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input
              required
              type="text"
              placeholder="Adınız Soyadınız"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="bg-[#141414] border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-white/30"
              data-testid="contact-form-name"
            />
            <input
              required
              type="email"
              placeholder="E-posta"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="bg-[#141414] border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-white/30"
              data-testid="contact-form-email"
            />
            <input
              type="text"
              placeholder="Telefon"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="bg-[#141414] border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-white/30 sm:col-span-2"
              data-testid="contact-form-phone"
            />
            <textarea
              required
              placeholder="Mesajınız"
              rows={6}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="bg-[#141414] border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-white/30 sm:col-span-2"
              data-testid="contact-form-message"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="brand-btn mt-6 px-8 py-3 text-sm uppercase tracking-[0.2em] disabled:opacity-50"
            data-testid="contact-form-submit"
          >
            {submitting ? "Gönderiliyor..." : "Gönder"}
          </button>
        </form>
      </div>
    </div>
  );
}
